import { useState } from "react";
import { Alert, Form, Input, message } from "antd";
import { EyeFilled, LockFilled } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { Auth } from "../../../authentication/amplifyAuth";
import { API } from "../../../helpers/API/amplifyApi";
import { useAuth } from "../../../authentication/authHelpers";
import { passwordRegex } from "../../dashboard/utils/regex";
import MondayButton from "../../commonComponents/MondayButton/MondayButton";
import {
  getAuthIdentityId,
  isForceResetRequired,
} from "../../../utils/forceReset";
import { eventTracker } from "../../../utils/eventTracker";
import "./ChangePasswordForm.scss";

const PASSWORD_HINT =
  "Të paktën 8 karaktere, një shkronjë të vogël, një të madhe dhe një karakter të veçantë (@#$%^&+*!=).";

function clearForceResetLocally(updateAuthCtx) {
  if (typeof updateAuthCtx !== "function") return;
  updateAuthCtx((prev) => {
    if (!prev || typeof prev !== "object") return prev;
    const user = prev.user
      ? { ...prev.user, forceReset: false }
      : prev.user;
    const userAccess = Array.isArray(prev.userAccess)
      ? prev.userAccess.map((u) =>
          u ? { ...u, forceReset: false } : u
        )
      : prev.userAccess;
    return { ...prev, user, userAccess, forceReset: false };
  });
}

/**
 * Change Cognito password: old + new + confirm.
 * @param {"voluntary"|"forced"} mode
 */
function ChangePasswordForm({ mode = "voluntary", onSuccess }) {
  const [form] = Form.useForm();
  const auth = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);

  const goForgotPassword = async () => {
    try {
      await auth?.signout?.(() => navigate("/harruatkodin"));
    } catch {
      try {
        await Auth.signOut();
      } catch {
        /* ignore */
      }
      navigate("/harruatkodin");
    }
  };

  const onFinish = async (values) => {
    setSaving(true);
    const identityId = getAuthIdentityId(auth);
    const forced = mode === "forced" || isForceResetRequired(auth);

    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(user, values.oldPassword, values.password);

      if (forced && identityId) {
        await API.put("users", `/users/${identityId}`, {
          body: { forceReset: false },
        });
        clearForceResetLocally(auth?.updateAuthCtx);
      }

      eventTracker("Profile", "click", "Changed password");
      message.success("Fjalëkalimi u ndryshua me sukses!");
      form.resetFields();
      if (typeof onSuccess === "function") {
        onSuccess();
      } else if (forced) {
        navigate("/kryefaqja", { replace: true });
      }
    } catch (err) {
      console.error("changePassword failed", err);
      const code = err?.name || err?.code || "";
      if (
        code === "NotAuthorizedException" ||
        /incorrect|not authorized|wrong/i.test(String(err?.message || ""))
      ) {
        message.error("Fjalëkalimi i vjetër nuk është i saktë.");
      } else if (
        code === "InvalidPasswordException" ||
        /password/i.test(String(err?.message || ""))
      ) {
        message.error(
          "Fjalëkalimi i ri nuk plotëson rregullat e sigurisë."
        );
      } else {
        message.error("Ndodhi një gabim në ndryshimin e fjalëkalimit!");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={`changePasswordForm changePasswordForm--${mode}`}>
      {mode === "forced" ? (
        <Alert
          type="warning"
          showIcon
          banner
          message="Duhet të ndryshoni fjalëkalimin për të vazhduar."
          description={PASSWORD_HINT}
          style={{ marginBottom: 16 }}
        />
      ) : (
        <Alert
          type="info"
          showIcon
          message="Ndrysho fjalëkalimin"
          description={PASSWORD_HINT}
          style={{ marginBottom: 16 }}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        name={`change-password-${mode}`}
        onFinish={onFinish}
        requiredMark={false}
      >
        <Form.Item
          name="oldPassword"
          label="Fjalëkalimi i vjetër"
          rules={[
            { required: true, message: "Shkruani fjalëkalimin e vjetër." },
          ]}
        >
          <Input.Password
            placeholder="Fjalëkalimi i vjetër"
            prefix={<LockFilled />}
            autoComplete="current-password"
            iconRender={(visible) => (visible ? <EyeFilled /> : <EyeFilled />)}
          />
        </Form.Item>

        <Form.Item
          name="password"
          label="Fjalëkalimi i ri"
          rules={[
            {
              required: true,
              pattern: passwordRegex,
              message:
                "Fjalëkalimi i ri duhet të ketë min. 8 karaktere, shkronjë të vogël, të madhe dhe karakter të veçantë.",
            },
          ]}
        >
          <Input.Password
            placeholder="Fjalëkalimi i ri"
            prefix={<LockFilled />}
            autoComplete="new-password"
            iconRender={(visible) => (visible ? <EyeFilled /> : <EyeFilled />)}
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="Konfirmo fjalëkalimin e ri"
          dependencies={["password"]}
          rules={[
            { required: true, message: "Konfirmoni fjalëkalimin e ri." },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error("Fjalëkalimet e reja nuk përputhen.")
                );
              },
            }),
          ]}
        >
          <Input.Password
            placeholder="Konfirmo fjalëkalimin e ri"
            prefix={<LockFilled />}
            autoComplete="new-password"
            iconRender={(visible) => (visible ? <EyeFilled /> : <EyeFilled />)}
          />
        </Form.Item>

        <div className="changePasswordForm__actions">
          <MondayButton
            className="mondayButtonGreen"
            htmlType="submit"
            spin={saving}
          >
            Ruaj
          </MondayButton>
        </div>

        <div className="changePasswordForm__forgot">
          <span>Nuk e mbani mend fjalëkalimin? </span>
          <button
            type="button"
            className="changePasswordForm__forgotLink"
            onClick={goForgotPassword}
          >
            Harrova fjalëkalimin
          </button>
        </div>
      </Form>
    </div>
  );
}

export default ChangePasswordForm;
